import { Outlet, type RouteObject } from 'react-router'
import { LoadingState } from '@/components/states'
import { RequireAuth, RequireOnboarded, RequireRole } from '@/features/auth/guards'
import { RootLayout } from './layout/RootLayout'
import { RouteErrorPage } from './RouteErrorPage'

// Cada página é carregada sob demanda (o painel administrativo não pesa para o visitante).
type PageModule = { default: React.ComponentType }
const page = (load: () => Promise<PageModule>): Pick<RouteObject, 'lazy'> => ({
  lazy: async () => ({ Component: (await load()).default }),
})

const authenticated = (
  <RequireAuth>
    <Outlet />
  </RequireAuth>
)

const onboarded = (
  <RequireAuth>
    <RequireOnboarded>
      <Outlet />
    </RequireOnboarded>
  </RequireAuth>
)

const team = (
  <RequireAuth>
    <RequireRole role="moderator">
      <Outlet />
    </RequireRole>
  </RequireAuth>
)

const adminOnly = (
  <RequireRole role="admin">
    <Outlet />
  </RequireRole>
)

/** Rotas do site, usadas pelo navegador (router.tsx) e pela pré-renderização do build (prerender.tsx). */
export const routes: RouteObject[] = [
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <RouteErrorPage />,
    hydrateFallbackElement: <LoadingState />,
    children: [
      { index: true, ...page(() => import('@/features/home/HomePage')) },
      { path: 'propostas', ...page(() => import('@/features/proposals/ProposalsPage')) },
      { path: 'propostas/:slug', ...page(() => import('@/features/proposals/ProposalPage')) },
      { path: 'participantes/:username', ...page(() => import('@/features/profile/PublicProfilePage')) },
      { path: 'entrar', ...page(() => import('@/features/auth/SignInPage')) },

      { path: 'sobre', ...page(() => import('@/features/pages/AboutPage')) },
      { path: 'como-funciona', ...page(() => import('@/features/pages/HowItWorksPage')) },
      { path: 'regras', ...page(() => import('@/features/pages/RulesPage')) },
      { path: 'termos', ...page(() => import('@/features/pages/TermsPage')) },
      { path: 'privacidade', ...page(() => import('@/features/pages/PrivacyPage')) },
      { path: 'moderacao', ...page(() => import('@/features/pages/ModerationLogPage')) },

      {
        element: authenticated,
        children: [
          { path: 'cadastro', ...page(() => import('@/features/auth/OnboardingPage')) },
          { path: 'conta', ...page(() => import('@/features/account/AccountPage')) },
        ],
      },
      {
        element: onboarded,
        children: [
          { path: 'propor', ...page(() => import('@/features/submit/SubmitProposalPage')) },
          { path: 'propor/:id/editar', ...page(() => import('@/features/submit/EditSubmissionPage')) },
        ],
      },
      {
        path: 'admin',
        element: team,
        children: [
          {
            ...page(() => import('@/features/admin/AdminLayout')),
            children: [
              { index: true, ...page(() => import('@/features/admin/AdminDashboardPage')) },
              { path: 'propostas', ...page(() => import('@/features/admin/AdminProposalsPage')) },
              { path: 'propostas/:id', ...page(() => import('@/features/admin/AdminProposalPage')) },
              { path: 'moderacao', ...page(() => import('@/features/admin/AdminModerationPage')) },
              { path: 'recursos', ...page(() => import('@/features/admin/AdminAppealsPage')) },
              { path: 'usuarios', ...page(() => import('@/features/admin/AdminUsersPage')) },
              {
                element: adminOnly,
                children: [
                  { path: 'categorias', ...page(() => import('@/features/admin/AdminCategoriesPage')) },
                  { path: 'auditoria', ...page(() => import('@/features/admin/AdminAuditPage')) },
                  { path: 'configuracoes', ...page(() => import('@/features/admin/AdminSettingsPage')) },
                ],
              },
            ],
          },
        ],
      },

      { path: '*', ...page(() => import('@/features/pages/NotFoundPage')) },
    ],
  },
]
